import React, { useEffect, useState } from "react";
import { CreditCard, Eye, EyeOff, Trash2, CheckCircle2, AlertCircle, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

/**
 * Super Admin — Platform Razorpay credentials.
 *
 * These keys route platform-collected payments (tenant trial→paid
 * upgrades, SMS-wallet recharges) into the PLATFORM's own Razorpay
 * settlement account. Distinct from each tenant's own Razorpay keys
 * (configured at /admin/branding) which route end-customer payments
 * into the tenant's account.
 */
export default function SuperAdminPayments() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [showWebhook, setShowWebhook] = useState(false);
  const [form, setForm] = useState({
    razorpay_key_id: "",
    razorpay_key_secret: "",
    razorpay_webhook_secret: "",
  });

  const refresh = async () => {
    try {
      const { data } = await api.get("/superadmin/payments/razorpay");
      setStatus(data);
      setForm({
        razorpay_key_id: data.razorpay_key_id || "",
        razorpay_key_secret: "",
        razorpay_webhook_secret: "",
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Razorpay status");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const save = async () => {
    const payload = {};
    if (form.razorpay_key_id.trim()) payload.razorpay_key_id = form.razorpay_key_id.trim();
    if (form.razorpay_key_secret) payload.razorpay_key_secret = form.razorpay_key_secret;
    if (form.razorpay_webhook_secret) payload.razorpay_webhook_secret = form.razorpay_webhook_secret;
    if (Object.keys(payload).length === 0) {
      toast.error("Enter at least one field to save");
      return;
    }
    setSaving(true);
    try {
      await api.put("/superadmin/payments/razorpay", payload);
      toast.success("Razorpay credentials saved & verified");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    if (!window.confirm("Clear all platform Razorpay credentials? Trial upgrades will fall back to mock until you re-enter keys.")) return;
    try {
      await api.delete("/superadmin/payments/razorpay");
      toast.success("Razorpay credentials cleared");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clear failed");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]" data-testid="sa-payments-loading">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  const configured = status?.configured;
  const mode = status?.razorpay_mode;

  return (
    <div className="max-w-3xl mx-auto py-8" data-testid="sa-payments-page">
      <div className="flex items-center gap-3 mb-2">
        <CreditCard size={26} />
        <h1 className="font-display text-3xl" data-testid="sa-payments-title">Platform Payments — Razorpay</h1>
      </div>
      <p className="text-sm text-[hsl(var(--kb-muted))] mb-8 leading-relaxed">
        Enter your <b>platform-level Razorpay keys</b>. These are used to receive payments <b>from tenants</b> —
        plan upgrades, SMS-wallet recharges, and other SaaS billing — directly into your own Razorpay settlement
        account. Distinct from each tenant&apos;s own Razorpay keys (configured at <code>/admin/branding</code>)
        which route customer payments into that tenant&apos;s account.
      </p>

      {/* Status panel */}
      <div className={`border rounded-lg p-5 mb-6 flex items-start gap-3 ${
        configured ? "bg-green-50 border-green-200" : "bg-amber-50 border-amber-200"
      }`} data-testid="sa-payments-status-panel">
        {configured ? (
          <CheckCircle2 className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
        ) : (
          <AlertCircle className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
        )}
        <div className="text-sm">
          <div className="font-semibold">
            {configured ? "Razorpay configured" : "Razorpay not configured"}
          </div>
          {configured && (
            <div className="text-xs text-[hsl(var(--kb-muted))] mt-1">
              key_id: <code data-testid="sa-payments-current-keyid">{status?.razorpay_key_id}</code>
              {" · "}
              mode: <span className={`font-semibold ${mode === "live" ? "text-red-600" : "text-amber-700"}`} data-testid="sa-payments-mode">
                {mode === "live" ? "LIVE" : mode === "test" ? "TEST" : "unknown"}
              </span>
              {status?.razorpay_webhook_secret_set && " · webhook ✓"}
            </div>
          )}
          {!configured && (
            <div className="text-xs text-[hsl(var(--kb-muted))] mt-1">
              Tenants will fall back to a mock upgrade flow until you configure live keys here.
            </div>
          )}
        </div>
      </div>

      {/* Form */}
      <div className="border rounded-lg p-6 space-y-5 bg-white" data-testid="sa-payments-form">
        <div>
          <label className="block text-xs uppercase tracking-wider text-[hsl(var(--kb-muted))] mb-1">
            Razorpay Key ID
          </label>
          <input
            type="text"
            placeholder="rzp_test_xxxxxxxxxxxx OR rzp_live_xxxxxxxxxxxx"
            value={form.razorpay_key_id}
            onChange={(e) => setForm({ ...form, razorpay_key_id: e.target.value })}
            className="kb-input w-full font-mono text-sm"
            data-testid="sa-payments-keyid-input"
          />
          <p className="text-[10px] text-[hsl(var(--kb-muted))] mt-1">
            From Razorpay Dashboard → Account &amp; Settings → API Keys.
          </p>
        </div>

        <div>
          <label className="block text-xs uppercase tracking-wider text-[hsl(var(--kb-muted))] mb-1">
            Razorpay Key Secret
            {status?.razorpay_key_secret_set && !form.razorpay_key_secret && (
              <span className="ml-2 text-green-700 normal-case text-[10px]">(already set — leave blank to keep)</span>
            )}
          </label>
          <div className="relative">
            <input
              type={showSecret ? "text" : "password"}
              placeholder={status?.razorpay_key_secret_set ? "•••••• (saved)" : "Paste secret"}
              value={form.razorpay_key_secret}
              onChange={(e) => setForm({ ...form, razorpay_key_secret: e.target.value })}
              className="kb-input w-full font-mono text-sm pr-10"
              data-testid="sa-payments-secret-input"
            />
            <button
              type="button"
              onClick={() => setShowSecret((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[hsl(var(--kb-muted))]"
              aria-label={showSecret ? "Hide secret" : "Show secret"}
              data-testid="sa-payments-secret-toggle"
            >
              {showSecret ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <p className="text-[10px] text-[hsl(var(--kb-muted))] mt-1">
            Stored encrypted at rest. Never exposed to tenants or storefronts.
          </p>
        </div>

        <div>
          <label className="block text-xs uppercase tracking-wider text-[hsl(var(--kb-muted))] mb-1">
            Webhook Secret <span className="normal-case text-[10px]">(optional — for refund / subscription reconciliation)</span>
            {status?.razorpay_webhook_secret_set && !form.razorpay_webhook_secret && (
              <span className="ml-2 text-green-700 normal-case text-[10px]">(already set)</span>
            )}
          </label>
          <div className="relative">
            <input
              type={showWebhook ? "text" : "password"}
              placeholder={status?.razorpay_webhook_secret_set ? "•••••• (saved)" : "Paste webhook secret"}
              value={form.razorpay_webhook_secret}
              onChange={(e) => setForm({ ...form, razorpay_webhook_secret: e.target.value })}
              className="kb-input w-full font-mono text-sm pr-10"
              data-testid="sa-payments-webhook-input"
            />
            <button
              type="button"
              onClick={() => setShowWebhook((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[hsl(var(--kb-muted))]"
              aria-label={showWebhook ? "Hide webhook secret" : "Show webhook secret"}
              data-testid="sa-payments-webhook-toggle"
            >
              {showWebhook ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <div className="mt-2 text-[11px] text-[hsl(var(--kb-muted))] bg-[hsl(var(--kb-surface-alt))] border border-[hsl(var(--kb-border))] rounded px-2 py-1.5">
            <div className="font-semibold mb-0.5">Webhook URL to register in Razorpay Dashboard:</div>
            <code className="font-mono text-[10px] break-all select-all" data-testid="sa-payments-webhook-url">
              {(typeof window !== "undefined" ? window.location.origin : "")}/api/payments/razorpay/webhook?platform=1
            </code>
            <div className="mt-1 text-[10px]">
              Subscribe to events: <code className="text-[10px]">payment.captured</code>, <code className="text-[10px]">payment.failed</code>, <code className="text-[10px]">refund.processed</code>.
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 pt-2">
          <button
            onClick={save}
            disabled={saving}
            className="kb-btn-primary disabled:opacity-60"
            data-testid="sa-payments-save-btn"
          >
            {saving ? "Verifying…" : "Save & Verify"}
          </button>
          {configured && (
            <button
              onClick={clear}
              className="text-xs text-red-600 hover:underline flex items-center gap-1"
              data-testid="sa-payments-clear-btn"
            >
              <Trash2 size={12} /> Clear credentials
            </button>
          )}
          <a
            href="https://dashboard.razorpay.com/app/keys"
            target="_blank"
            rel="noreferrer"
            className="ml-auto text-xs text-[hsl(var(--kb-muted))] hover:text-[hsl(var(--kb-fg))] flex items-center gap-1"
            data-testid="sa-payments-dashboard-link"
          >
            Razorpay Dashboard <ExternalLink size={12} />
          </a>
        </div>
      </div>

      <div className="text-xs text-[hsl(var(--kb-muted))] mt-6 leading-relaxed">
        <p className="font-semibold mb-1">What these keys do:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>When a tenant on a free trial clicks &quot;Upgrade&quot; in <code>/admin/upgrade</code>, the backend creates a Razorpay Order against THESE keys and opens Razorpay Checkout on their side.</li>
          <li>Money settles into <b>your</b> Razorpay account, not the tenant&apos;s.</li>
          <li>Same flow applies to tenant SMS-wallet recharges and recurring subscription renewals.</li>
          <li>Saving fires a real <code>orders.all</code> request to Razorpay to validate the credentials — typos and wrong-environment keys fail immediately.</li>
        </ul>
      </div>
    </div>
  );
}
